import React, {
	FC,
	createContext,
	useContext,
	useCallback,
	useImperativeHandle,
	useRef,
	useState,
	useEffect,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
	BasicMatingTableItem,
	BasicMoveListTableItem,
	IBasicMoveListSetting,
	IDistriwinAllAnimalsListSetting,
	IMoveEvent,
	MoveEvent,
} from 'shared/api/api';
import { WebAppState } from 'web/state/store.web';
import { GetBasicMoveListWorklistPdf } from 'web/web-helpers/pdf-helper/work-list-pdf-helper';
import { FlexColumn } from '../../basic-flex/flex-column';
import { BasicMoveListHeader } from './basic-move-list-header';
import { BasicMoveListTable } from './basic-move-list-table';
import { SetCheckedCount } from 'shared/state/ducks/stem-animals/operations';
import { UpsertManyMoveEvents } from 'shared/state/ducks/move-events/operations';
import ObjectID from 'bson-objectid';
import { localized } from 'shared/state/i18n/i18n';
import { showDefaultAlert } from '../../skiold-alert/skiold-alert';

interface BasicMoveListContextProps {
	fromSectionId: string;
	fromPenId: string;
	toSectionId: string;
	toPenId: string;
	setFromSectionId: (id: string) => void;
	setFromPenId: (id: string) => void;
	setToSectionId: (id: string) => void;
	setToPenId: (id: string) => void;
	// setFromSectionUsesPens: (usesPens: boolean) => void;
	// setToSectionUsesPens: (usesPens: boolean) => void;
	// fromSectionUsesPens?: boolean;
	// toSectionUsesPens?: boolean;
	filteredData: BasicMoveListTableItem[];
	setFilteredData: (data: BasicMoveListTableItem[]) => void;
	selected: BasicMoveListTableItem[];
	setSelected: (data: BasicMoveListTableItem[]) => void;
	print: () => Promise<void>;
	saveMoveEvents: () => Promise<void>;
	skioldRef: React.MutableRefObject<any>;
	setting: IBasicMoveListSetting;
	moveDate: Date;
	setMoveDate: (date: Date) => void;
	setAnimalsInPens: React.Dispatch<React.SetStateAction<BasicMoveListTableItem[]>>;
	animalsInPens: BasicMoveListTableItem[];
}

const BasicMoveListContext = createContext<BasicMoveListContextProps | undefined>(undefined);

export const useBasicMoveList = () => {
	const ctx = useContext(BasicMoveListContext);
	if (!ctx) {
		throw new Error('useBasicMoveList must be used within BasicMoveListProvider');
	}
	return ctx;
};

export const BasicMoveListProvider: FC<{ ref?: any; setting: IBasicMoveListSetting; children: React.ReactNode }> = ({
	setting,
	children,
	ref,
}) => {
	const dispatch = useDispatch();
	const [fromSectionId, setFromSectionId] = useState<string>('');
	const [fromPenId, setFromPenId] = useState<string>('');
	const [toSectionId, setToSectionId] = useState<string>('');
	const [toPenId, setToPenId] = useState<string>('');
	const [moveDate, setMoveDate] = useState<Date>(new Date());
	// const [fromSectionUsesPens, setFromSectionUsesPens] = useState<boolean>(false);
	// const [toSectionUsesPens, setToSectionUsesPens] = useState<boolean>(false);

	useEffect(() => {}, [fromSectionId]);
	const [animalsInPens, setAnimalsInPens] = React.useState<BasicMoveListTableItem[]>([]);
	const [filteredData, setFilteredData] = useState<BasicMoveListTableItem[]>([]);
	const [selected, setSelected] = useState<BasicMoveListTableItem[]>([]);
	const profile = useSelector((state: WebAppState) => state.profile.active!);
	const skioldRef = useRef<any>(null);
	const print = useCallback(async () => {
		const filteredDataUpdated = animalsInPens.filter(animalInPen =>
			filteredData.some(selectedItem => selectedItem.stemAnimalId === animalInPen.stemAnimalId),
		);

		await GetBasicMoveListWorklistPdf(
			filteredDataUpdated,
			'BasicMoveListWorklist.pdf',
			profile.siteId!,
			Intl.DateTimeFormat().resolvedOptions().timeZone,
			profile.language,
		);
	}, [filteredData, profile, animalsInPens]);

	const saveMoveEvents = useCallback(async () => {
		// Map selected selected to animmalsInPens
		const moveEventsToSave = animalsInPens
			.filter(animalInPen =>
				selected.some(selectedItem => selectedItem.stemAnimalId === animalInPen.stemAnimalId),
			)
			.map(animalInPen => {
				const moveEvent = MoveEvent.fromJS({});
				if (!animalInPen.stemAnimalId || !animalInPen.toPenId) {
					showDefaultAlert(localized('missingDataForMoveEvent'));
					throw new Error('Missing data for move event');
				}
				moveEvent.init({
					id: new ObjectID().toHexString(),
					stemAnimalId: animalInPen.stemAnimalId,
					penId: animalInPen.toPenId,
					moveDate: animalInPen.moveDate ? animalInPen.moveDate : new Date(),
					siteId: profile.siteId!,
				});
				return moveEvent;
			});

		dispatch(UpsertManyMoveEvents(moveEventsToSave));
		return Promise.resolve();
	}, [animalsInPens, dispatch, profile.siteId, selected]);

	useEffect(() => {
		dispatch(SetCheckedCount(selected.length, true));
	}, [dispatch, selected]);

	useImperativeHandle(
		ref,
		() => ({
			print() {
				print();
			},
			save() {
				const result = saveMoveEvents();
				if (skioldRef.current && result) {
					result.then(() => {
						skioldRef.current.ResetSelected([]);
					});
				}
			},
			reset() {
				setFromSectionId('');
				setFromPenId('');
				setToSectionId('');
				setToPenId('');
				setMoveDate(new Date());
				setSelected([]);
			},
		}),
		[print, saveMoveEvents],
	);

	return (
		<BasicMoveListContext.Provider
			value={{
				fromSectionId,
				fromPenId,
				toSectionId,
				toPenId,
				setFromSectionId,
				setFromPenId,
				setToSectionId,
				setToPenId,
				filteredData,
				setFilteredData,
				selected,
				setSelected,
				print,
				saveMoveEvents,
				skioldRef,
				setting,
				moveDate,
				setMoveDate,
				setAnimalsInPens,
				animalsInPens,
				// fromSectionUsesPens,
				// toSectionUsesPens,
				// setFromSectionUsesPens,
				// setToSectionUsesPens,
			}}
		>
			{children}
		</BasicMoveListContext.Provider>
	);
};
